import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createDashboardSession, DashboardSession, DashboardSessionContext } from './DashboardSessionContext';

// Define custom types that we need
interface AxiosRequestConfig {
  method?: string;
  url?: string;
  data?: any;
  headers?: Record<string, string>;
}

// Define response types
interface AuthResponse {
  token: string;
  roles?: string[];
  role?: string; // Keep for backward compatibility
}

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  apiRequest: <T>(config: AxiosRequestConfig) => Promise<T>;
  userRoles: string[];
  userRole: string | null; // Deprecated, but kept for compatibility
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  loading: true,
  token: null,
  userRoles: [],
  userRole: null,
  hasRole: () => false,
  isAdmin: false,
  login: async () => {},
  logout: () => {},
  apiRequest: async () => { throw new Error('Not implemented'); },
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const navigate = useNavigate();
  const dashboardSession = useRef<DashboardSession>({ ...createDashboardSession(), active: false });
  const authEpoch = useRef(0);
  const observedToken = useRef<string | null | undefined>(undefined);
  const synchronizeSharedToken = useRef<() => void>(() => undefined);
  const changeSessionToken = (value: string | null) => {
    dashboardSession.current.active = false;
    dashboardSession.current.ready = {};
    const owner: DashboardSession = { ...createDashboardSession(), active: value !== null };
    owner.isCurrent = () => {
      if (localStorage.getItem('token') !== value) {
        synchronizeSharedToken.current();
        return false;
      }
      return owner.active;
    };
    // Capture the auth identity in a guard closure, never in the figure cache.
    owner.acceptsAuthorization = authorization => Boolean(owner.isCurrent?.()) &&
      authorization === (value ? `Bearer ${value}` : null);
    dashboardSession.current = owner;
    setToken(value);
  };

  // Use environment variable for API URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // Helper functions
  const hasRole = (role: string): boolean => {
    return userRoles.includes(role);
  };

  const isAdmin = hasRole('admin');
  const userRole = userRoles.length > 0 ? userRoles[0] : null; // For backward compatibility

  // The shared request client reads storage per dispatch, including changes
  // from other tabs. Rotate the owner before verifying any replacement token.
  useEffect(() => {
    const synchronize = async (force = false) => {
      const stored = localStorage.getItem('token');
      if (!force && stored === observedToken.current) return;
      observedToken.current = stored;
      const epoch = ++authEpoch.current;
      changeSessionToken(null);
      setIsAuthenticated(false);
      setUserRoles([]);
      setLoading(Boolean(stored));
      if (!stored) return;
      try {
        const response = await axios.post(`${API_URL}/auth/verify`, { token: stored });
        if (epoch !== authEpoch.current) return;
        if (localStorage.getItem('token') !== stored) {
          void synchronize();
          return;
        }
        setIsAuthenticated(true);
        changeSessionToken(stored);
        const responseData = response.data as { roles?: string[], role?: string };
        setUserRoles(responseData.roles ?? (responseData.role ? [responseData.role] : []));
      } catch (error) {
        if (epoch !== authEpoch.current) return;
        if (localStorage.getItem('token') !== stored) {
          void synchronize();
          return;
        }
        localStorage.removeItem('token');
        observedToken.current = null;
        changeSessionToken(null);
      } finally {
        if (epoch === authEpoch.current) setLoading(false);
      }
    };
    synchronizeSharedToken.current = () => { void synchronize(); };
    const onStorage = (event: StorageEvent) => {
      if ((event.key === 'token' || event.key === null) &&
          (!event.storageArea || event.storageArea === localStorage)) void synchronize();
    };
    window.addEventListener('storage', onStorage);
    void synchronize(true);
    return () => {
      authEpoch.current += 1;
      synchronizeSharedToken.current = () => undefined;
      window.removeEventListener('storage', onStorage);
    };
  }, [API_URL]);

  // Generic API request function with improved error handling
  const apiRequest = async <T,>(config: AxiosRequestConfig): Promise<T> => {
    const headers: Record<string, string> = {...(config.headers || {})};
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      console.log(`Making ${config.method || 'GET'} request to: ${API_URL}${config.url || ''}`);
      
      const response = await axios({
        ...config,
        headers,
        url: `${API_URL}${config.url || ''}`,
        // Add responseType to help with non-JSON responses
        responseType: 'json',
        // Add validation to prevent auto-redirect to HTML error pages
        validateStatus: (status) => status < 500
      });
      
      return response.data as T;
    } catch (error: any) {
      console.error('API request error:', error);
      
      if (error.response) {
        // Log the full response for debugging
        console.error('Error response:', {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data
        });
        
        // Server responded with a non-2xx status
        if (error.response.status === 401) {
          // Unauthorized - token might be expired
          logout();
        }
        
        // Check if the response is HTML (improved detection)
        const contentType = error.response.headers['content-type'];
        if (contentType && contentType.includes('text/html')) {
          console.error('Server returned HTML instead of JSON:', 
            typeof error.response.data === 'string' 
              ? error.response.data.substring(0, 200) // Show first 200 chars of HTML
              : 'Non-string HTML response');
              
          throw new Error(`Server returned HTML instead of JSON (status ${error.response.status}). The server might be experiencing an error.`);
        }
        
        // Special handling for 500 errors which might return HTML
        if (error.response.status >= 500) {
          throw new Error(`Server error (${error.response.status}). Please try again later or contact support.`);
        }
        
        // If there's an error message in the response
        if (error.response.data) {
          if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE html>')) {
            throw new Error(`Server returned HTML error page with status ${error.response.status}. Please check server logs.`);
          }
          
          if (error.response.data.message) {
            throw new Error(error.response.data.message);
          }
        }

        throw new Error(`Request failed with status ${error.response.status}`);
      } else if (error.request) {
        // Request was made but no response received
        console.error('No response received:', error.request);
        throw new Error('No response received from the server. Please check your connection.');
      } else {
        // Something else happened
        throw error;
      }
    }
  };

  const login = async (username: string, password: string) => {
    const epoch = ++authEpoch.current;
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password,
      });
      
      const data = response.data as AuthResponse;
      if (epoch !== authEpoch.current) return;
      
      localStorage.setItem('token', data.token);
      observedToken.current = data.token;
      console.log('Token stored successfully');
      
      setIsAuthenticated(true);
      changeSessionToken(data.token);
      setLoading(false);
      
      // Handle roles from the response
      if (data.roles) {
        setUserRoles(data.roles);
      } else if (data.role) {
        // Fallback for old single role format
        setUserRoles([data.role]);
      }


      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    authEpoch.current += 1;
    localStorage.removeItem('token');
    observedToken.current = null;
    setIsAuthenticated(false);
    changeSessionToken(null);
    setLoading(false);
    setUserRoles([]);
    navigate('/login');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      loading, 
      token, 
      userRoles,
      userRole, // Backward compatibility
      hasRole,
      isAdmin,
      login,
      logout,
      apiRequest
    }}>
      <DashboardSessionContext.Provider value={dashboardSession.current}>
        {children}
      </DashboardSessionContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
